import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Container, Row, Col, Card, Badge, Table, Button, Spinner, Alert, Form, ProgressBar, Tabs, Tab } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart, LineChart, PieChart, FunnelChart, RadarChart } from 'echarts/charts';
import {
  GridComponent, TooltipComponent, TitleComponent, LegendComponent, DataZoomComponent, MarkLineComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import budgetService from '../../../services/budgetService';
import ClaroDigitalReportTab from './ClaroDigitalReportTab';
import { useAuth } from '../../../contexts/AuthContext';

echarts.use([BarChart, LineChart, PieChart, FunnelChart, RadarChart, GridComponent, TooltipComponent, TitleComponent, LegendComponent, DataZoomComponent, MarkLineComponent, CanvasRenderer]);

// ==========================================
// HELPERS
// ==========================================
const fmt = (n) => n == null ? '0' : Number(n).toLocaleString('es-EC');
const fmtP = (n) => n == null ? '0%' : `${(Number(n) * 100).toFixed(1)}%`;
const fmtC = (n) => n == null ? '$0' : `$${Number(n).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtK = (n) => { if (n == null) return '0'; const v = Number(n); if (v >= 1e6) return `${(v/1e6).toFixed(1)}M`; if (v >= 1e3) return `${(v/1e3).toFixed(1)}K`; return v.toLocaleString('es-EC'); };

const PRODUCT_COLORS = { EQUIPOS: '#00d97e', HOGAR: '#f5803e', POSTPAGO: '#6b5eae', PREPAGO: '#27bcfd', PORTABILIDAD: '#e63757' };
const CANAL_COLORS = { 'OTROS MEDIOS': '#6c757d', 'ORGANICO': '#28a745', 'POTENCIAL PAUTA': '#ffc107', 'PAUTA': '#dc3545', 'MEDIOS PROPIOS': '#17a2b8' };
const CLASIF_COLORS = { 'Acepta': '#28a745', 'No Acepta': '#dc3545', 'No acepta': '#dc3545', 'No Aplica': '#ffc107', 'No aplica': '#ffc107', 'Agendado': '#0dcaf0', 'No Contactados': '#6c757d' };

// ==========================================
// SUB-COMPONENTS
// ==========================================
const KpiCard = ({ title, value, subtitle, color = 'primary', icon, small }) => (
  <Card className="border-0 shadow-sm h-100">
    <Card.Body className={small ? 'p-2 text-center' : 'p-3'}>
      <div className={small ? '' : 'd-flex align-items-center justify-content-between'}>
        <div>
          <p className="text-muted mb-0" style={{ fontSize: small ? '0.7rem' : '0.8rem' }}>{title}</p>
          <h4 className={`mb-0 text-${color}`} style={{ fontWeight: 700, fontSize: small ? '1rem' : '1.3rem' }}>{value}</h4>
          {subtitle && <small className="text-muted" style={{ fontSize: '0.7rem' }}>{subtitle}</small>}
        </div>
        {icon && !small && (
          <div className={`bg-${color} bg-opacity-10 rounded-3 p-2`}>
            <i className={`fas ${icon} text-${color}`} style={{ fontSize: '1.3rem' }}></i>
          </div>
        )}
      </div>
    </Card.Body>
  </Card>
);

const ChartCard = ({ title, icon, children, height = '320px' }) => (
  <Card className="border-0 shadow-sm h-100">
    <Card.Header className="bg-white border-0 py-2">
      <h6 className="mb-0"><i className={`fas ${icon} me-2 text-primary`}></i>{title}</h6>
    </Card.Header>
    <Card.Body className="p-2">{children}</Card.Body>
  </Card>
);

// ==========================================
// MAIN COMPONENT
// ==========================================
const ClaroJitDashboard = () => {
  const { hasRole, user } = useAuth();
  const canUpload = hasRole('admin') || hasRole('super_admin') || hasRole('ADMIN') || hasRole('SUPER_ADMIN') || hasRole('MANAGER') || user?.isSuperAdmin;
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // { progress, message, status }
  const [data, setData] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [execReport, setExecReport] = useState(null);
  const [execLoading, setExecLoading] = useState(false);
  const fileInputRef = useRef(null);

  // FILTROS
  const [filterCanal, setFilterCanal] = useState('');
  const [filterProducto, setFilterProducto] = useState('');
  const [filterTipo, setFilterTipo] = useState('');

  const filters = useMemo(() => {
    const f = {};
    if (filterCanal) f.canalValidado = filterCanal;
    if (filterProducto) f.grupoTransaccion = filterProducto;
    if (filterTipo) f.tipoProducto = filterTipo;
    return f;
  }, [filterCanal, filterProducto, filterTipo]);

  const hasFilters = filterCanal || filterProducto || filterTipo;

  // Opciones para filtros (derivadas de data)
  const filterOptions = useMemo(() => {
    if (!data) return { canales: [], productos: [], tipos: [] };
    return {
      canales: (data.byChannel || []).map(c => c.canalValidado).filter(Boolean),
      productos: (data.byProduct || []).map(p => p.grupoTransaccion).filter(Boolean),
      tipos: (data.byProductType || []).map(p => p.tipoProducto).filter(Boolean),
    };
  }, [data]);

  // DATA LOADING
  const loadPeriods = useCallback(async () => {
    const r = await budgetService.getJitPeriods();
    if (r.success) {
      const pd = r.data || [];
      setPeriods(pd);
      if (pd.length > 0 && !selectedPeriod) setSelectedPeriod(pd[0].periodo);
      else if (!pd.length) setLoading(false);
    } else setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUploads = useCallback(async () => {
    const r = await budgetService.getJitUploads();
    if (r.success) setUploads(r.data || []);
  }, []);

  const loadDashboard = useCallback(async () => {
    if (!selectedPeriod) { setLoading(false); return; }
    setLoading(true);
    try {
      const r = await budgetService.getJitDashboard({ periodoReporte: selectedPeriod, ...filters });
      if (r.success) setData(r.data);
      else toast.error(r.error);
    } catch { toast.error('Error de conexión'); }
    finally { setLoading(false); }
  }, [selectedPeriod, filters]);

  const loadExecutiveReport = useCallback(async () => {
    if (!selectedPeriod) return;
    setExecLoading(true);
    try {
      const r = await budgetService.getJitExecutiveReport(selectedPeriod);
      if (r.success) setExecReport(r.data);
    } catch { toast.error('Error al cargar reporte gerencial'); }
    finally { setExecLoading(false); }
  }, [selectedPeriod]);

  useEffect(() => { loadPeriods(); loadUploads(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedPeriod) loadDashboard(); }, [selectedPeriod, filters, loadDashboard]);
  useEffect(() => { if (activeTab === 'executive' && selectedPeriod && !execReport) loadExecutiveReport(); }, [activeTab, selectedPeriod, execReport, loadExecutiveReport]);
  useEffect(() => { setExecReport(null); }, [selectedPeriod]);

  // FILE UPLOAD (background con polling)
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().match(/\.(xlsx|xlsb)$/)) { toast.error('Solo .xlsx o .xlsb'); return; }
    setUploading(true);
    setUploadProgress({ progress: 2, message: 'Enviando archivo al servidor...', status: 'uploading' });
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await budgetService.uploadJitFile(fd);
      if (r.success && r.data?.jobId) {
        // Polling del status
        const jobId = r.data.jobId;
        setUploadProgress({ progress: 10, message: 'Procesando archivo...', status: 'parsing' });
        
        const poll = async () => {
          for (let attempt = 0; attempt < 300; attempt++) { // max ~10 minutos
            await new Promise(res => setTimeout(res, 2000));
            const statusR = await budgetService.getJitUploadStatus(jobId);
            if (statusR.success && statusR.data) {
              const st = statusR.data;
              setUploadProgress({ progress: st.progress, message: st.message, status: st.status });
              if (st.status === 'completed') {
                toast.success(st.message || 'Archivo cargado exitosamente');
                await loadPeriods(); await loadUploads();
                if (st.periodoReporte) setSelectedPeriod(st.periodoReporte);
                setUploadProgress(null);
                setUploading(false);
                await loadDashboard();
                return;
              }
              if (st.status === 'error') {
                toast.error(st.error || 'Error al procesar archivo');
                setUploadProgress(null);
                setUploading(false);
                return;
              }
            }
          }
          toast.warning('El proceso sigue en segundo plano. Recarga la página para ver los resultados.');
          setUploadProgress(null);
          setUploading(false);
        };
        poll();
      } else if (r.success) {
        // Respuesta directa (sin background)
        toast.success(r.message);
        await loadPeriods(); await loadUploads(); await loadDashboard();
        setUploadProgress(null);
        setUploading(false);
      } else {
        toast.error(r.error);
        setUploadProgress(null);
        setUploading(false);
      }
    } catch {
      toast.error('Error al cargar archivo');
      setUploadProgress(null);
      setUploading(false);
    }
    finally { if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleDeleteUpload = async (id, name) => {
    if (!window.confirm(`¿Eliminar "${name}"?`)) return;
    const r = await budgetService.deleteJitUpload(id);
    if (r.success) { toast.success(r.message); await loadPeriods(); await loadUploads(); await loadDashboard(); }
    else toast.error(r.error);
  };

  // ==========================================
  // CHART OPTIONS
  // ==========================================
  const kpis = data?.kpis;

  const dailyTrendOpt = useMemo(() => {
    if (!data?.byDay?.length) return {};
    const days = data.byDay.map(d => { const dt = new Date(d.fecha); return `${dt.getDate()}/${dt.getMonth()+1}`; });
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['Registros', 'Contactados', 'Aceptados'], bottom: 0 },
      grid: { left: '3%', right: '4%', bottom: '15%', containLabel: true },
      xAxis: { type: 'category', data: days, axisLabel: { rotate: 45, fontSize: 10 } },
      yAxis: [{ type: 'value', name: 'Cantidad' }, { type: 'value', name: 'Aceptados', position: 'right' }],
      series: [
        { name: 'Registros', type: 'bar', data: data.byDay.map(d => d.totalRegistros), itemStyle: { color: '#6c757d' }, barMaxWidth: 25, opacity: 0.6 },
        { name: 'Contactados', type: 'bar', data: data.byDay.map(d => d.totalContactados), itemStyle: { color: '#0d6efd' }, barMaxWidth: 25 },
        { name: 'Aceptados', type: 'line', yAxisIndex: 1, data: data.byDay.map(d => d.totalAcepta), itemStyle: { color: '#28a745' }, lineStyle: { width: 3 }, symbol: 'circle', symbolSize: 6, areaStyle: { opacity: 0.08 } },
      ],
      dataZoom: [{ type: 'inside', start: 0, end: 100 }],
    };
  }, [data]);

  const ratesTrendOpt = useMemo(() => {
    if (!data?.byDay?.length) return {};
    const days = data.byDay.map(d => { const dt = new Date(d.fecha); return `${dt.getDate()}/${dt.getMonth()+1}`; });
    return {
      tooltip: { trigger: 'axis', formatter: (p) => { let s = p[0].axisValue + '<br/>'; p.forEach(x => { s += `${x.marker} ${x.seriesName}: ${(x.value*100).toFixed(1)}%<br/>`; }); return s; }},
      legend: { data: ['Tasa Contacto', 'Tasa Aceptación'], bottom: 0 },
      grid: { left: '3%', right: '4%', bottom: '15%', containLabel: true },
      xAxis: { type: 'category', data: days, axisLabel: { rotate: 45, fontSize: 10 } },
      yAxis: { type: 'value', axisLabel: { formatter: v => `${(v*100).toFixed(0)}%` }, max: (v) => Math.min(v.max * 1.2, 1) },
      series: [
        { name: 'Tasa Contacto', type: 'line', data: data.byDay.map(d => d.tasaContacto), itemStyle: { color: '#0d6efd' }, lineStyle: { width: 2 }, areaStyle: { opacity: 0.05 }, smooth: true,
          markLine: { data: [{ type: 'average', name: 'Promedio' }], lineStyle: { type: 'dashed' }, label: { formatter: v => `${(v.value*100).toFixed(1)}%` } } },
        { name: 'Tasa Aceptación', type: 'line', data: data.byDay.map(d => d.tasaAceptacion), itemStyle: { color: '#28a745' }, lineStyle: { width: 2 }, areaStyle: { opacity: 0.05 }, smooth: true,
          markLine: { data: [{ type: 'average', name: 'Promedio' }], lineStyle: { type: 'dashed' }, label: { formatter: v => `${(v.value*100).toFixed(1)}%` } } },
      ],
    };
  }, [data]);

  const channelPieOpt = useMemo(() => {
    if (!data?.byChannel?.length) return {};
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      legend: { orient: 'vertical', left: 'left', top: 'middle', textStyle: { fontSize: 10 } },
      series: [{ type: 'pie', radius: ['35%', '65%'], center: ['60%', '50%'],
        data: data.byChannel.map(c => ({ name: c.canalValidado || 'N/A', value: c.totalRegistros, itemStyle: { color: CANAL_COLORS[c.canalValidado] || '#adb5bd' } })),
        label: { show: true, formatter: '{b}\n{d}%', fontSize: 10 }, emphasis: { itemStyle: { shadowBlur: 10 } } }],
    };
  }, [data]);

  const productBarOpt = useMemo(() => {
    if (!data?.byProduct?.length) return {};
    const prods = data.byProduct.slice(0, 10);
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['Registros', 'Contactados', 'Aceptados'], bottom: 0 },
      grid: { left: '3%', right: '4%', bottom: '12%', containLabel: true },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: prods.map(p => (p.grupoTransaccion || 'N/A').substring(0, 20)), inverse: true, axisLabel: { fontSize: 10 } },
      series: [
        { name: 'Registros', type: 'bar', data: prods.map(p => p.totalRegistros), itemStyle: { color: '#6c757d' }, barMaxWidth: 18 },
        { name: 'Contactados', type: 'bar', data: prods.map(p => p.totalContactados), itemStyle: { color: '#0d6efd' }, barMaxWidth: 18 },
        { name: 'Aceptados', type: 'bar', data: prods.map(p => p.totalAcepta), itemStyle: { color: '#28a745' }, barMaxWidth: 18 },
      ],
    };
  }, [data]);

  // Clasificación pie
  const clasifPieOpt = useMemo(() => {
    if (!kpis) return {};
    const items = [
      { name: 'Acepta', value: parseInt(kpis.totalAcepta) || 0 },
      { name: 'No Acepta', value: parseInt(kpis.totalNoAcepta) || 0 },
      { name: 'No Aplica', value: parseInt(kpis.totalNoAplica) || 0 },
      { name: 'Agendado', value: parseInt(kpis.totalAgendado) || 0 },
      { name: 'No Contactados', value: parseInt(kpis.totalNoContactados) || 0 },
    ].filter(i => i.value > 0);
    return {
      tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
      series: [{ type: 'pie', radius: ['30%', '60%'],
        data: items.map(i => ({ ...i, itemStyle: { color: CLASIF_COLORS[i.name] || '#adb5bd' } })),
        label: { formatter: '{b}\n{d}%', fontSize: 10 } }],
    };
  }, [kpis]);

  // Channel acceptance rate comparison
  const channelAcceptOpt = useMemo(() => {
    if (!data?.byChannel?.length) return {};
    const ch = data.byChannel.filter(c => c.canalValidado);
    return {
      tooltip: { trigger: 'axis', formatter: (p) => { let s = p[0].axisValue + '<br/>'; p.forEach(x => { s += `${x.marker} ${x.seriesName}: ${x.seriesName.includes('Tasa') ? (x.value*100).toFixed(1)+'%' : fmt(x.value)}<br/>`; }); return s; }},
      legend: { bottom: 0 },
      grid: { left: '3%', right: '5%', bottom: '12%', containLabel: true },
      xAxis: { type: 'category', data: ch.map(c => c.canalValidado), axisLabel: { rotate: 30, fontSize: 9 } },
      yAxis: [{ type: 'value', name: 'Cantidad' }, { type: 'value', name: 'Tasa', position: 'right', axisLabel: { formatter: v => `${(v*100).toFixed(0)}%` } }],
      series: [
        { name: 'Registros', type: 'bar', data: ch.map(c => c.totalRegistros), itemStyle: { color: '#6c757d' }, barMaxWidth: 30 },
        { name: 'Aceptados', type: 'bar', data: ch.map(c => c.totalAcepta), itemStyle: { color: '#28a745' }, barMaxWidth: 30 },
        { name: 'Tasa Aceptación', type: 'line', yAxisIndex: 1, data: ch.map(c => c.tasaAceptacion), itemStyle: { color: '#dc3545' }, lineStyle: { width: 3, type: 'dashed' }, symbol: 'diamond', symbolSize: 10 },
      ],
    };
  }, [data]);

  // Store ranking chart
  const storeRankOpt = useMemo(() => {
    if (!data?.byStore?.length) return {};
    const stores = data.byStore.slice(0, 10);
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: stores.map(s => (s.tienda || '').replace('Clic2Call_', '')), inverse: true, axisLabel: { fontSize: 10 } },
      series: [
        { type: 'bar', data: stores.map(s => ({ value: s.totalRegistros, itemStyle: { color: s.tasaAceptacion > 0.1 ? '#28a745' : s.tasaAceptacion > 0.05 ? '#ffc107' : '#dc3545' } })),
          barMaxWidth: 20, label: { show: true, position: 'right', formatter: (p) => `${fmt(p.value)} (${fmtP(stores[p.dataIndex].tasaAceptacion)})`, fontSize: 9 } },
      ],
    };
  }, [data]);

  // ==========================================
  // RENDER
  // ==========================================
  return (
    <Container fluid className="py-3">
      {/* HEADER */}
      <Row className="mb-2 align-items-center">
        <Col>
          <h4 className="mb-0"><i className="fas fa-phone-alt text-danger me-2"></i>Reporte JIT - SAC Claro</h4>
          <small className="text-muted">Gestión de leads y resultados del Contact Center</small>
        </Col>
        <Col xs="auto" className="d-flex gap-2 align-items-center">
          <Form.Select size="sm" value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)} style={{ width: 180 }}>
            <option value="">Seleccionar período</option>
            {periods.map(p => <option key={p.periodo} value={p.periodo}>{p.periodo} ({fmt(p.totalRecords)} reg.)</option>)}
          </Form.Select>
          <Button variant="outline-primary" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading || !canUpload}>
            {uploading ? <><Spinner animation="border" size="sm" className="me-1" /> Cargando...</> : <><i className="fas fa-upload me-1"></i> Subir JIT</>}
          </Button>
          {canUpload && <input ref={fileInputRef} type="file" accept=".xlsx,.xlsb" onChange={handleFileUpload} style={{ display: 'none' }} />}
        </Col>
      </Row>

      {/* FILTROS GLOBALES */}
      {data && kpis && (
        <Card className="border-0 shadow-sm mb-3">
          <Card.Body className="py-2 px-3">
            <Row className="align-items-center g-2">
              <Col xs="auto"><small className="text-muted fw-bold"><i className="fas fa-filter me-1"></i>Filtros:</small></Col>
              <Col xs={6} md={2}>
                <Form.Select size="sm" value={filterCanal} onChange={e => setFilterCanal(e.target.value)}>
                  <option value="">Todos los canales</option>
                  {filterOptions.canales.map(c => <option key={c} value={c}>{c}</option>)}
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Select size="sm" value={filterProducto} onChange={e => setFilterProducto(e.target.value)}>
                  <option value="">Todos los productos</option>
                  {filterOptions.productos.map(p => <option key={p} value={p}>{p}</option>)}
                </Form.Select>
              </Col>
              <Col xs={6} md={2}>
                <Form.Select size="sm" value={filterTipo} onChange={e => setFilterTipo(e.target.value)}>
                  <option value="">Todos los tipos</option>
                  {filterOptions.tipos.map(t => <option key={t} value={t}>{t}</option>)}
                </Form.Select>
              </Col>
              {hasFilters && (
                <Col xs="auto">
                  <Button variant="outline-secondary" size="sm" onClick={() => { setFilterCanal(''); setFilterProducto(''); setFilterTipo(''); }}>
                    <i className="fas fa-times me-1"></i>Limpiar
                  </Button>
                </Col>
              )}
              {hasFilters && (
                <Col xs="auto"><Badge bg="info">{[filterCanal, filterProducto, filterTipo].filter(Boolean).length} filtro(s) activo(s)</Badge></Col>
              )}
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Upload Progress */}
      {uploadProgress && (
        <Alert variant="primary" className="py-2 mb-2">
          <div className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <div className="flex-grow-1">
              <div className="d-flex justify-content-between mb-1">
                <small className="fw-bold">{uploadProgress.message}</small>
                <small>{uploadProgress.progress}%</small>
              </div>
              <ProgressBar now={uploadProgress.progress} animated striped variant="primary" style={{ height: 8 }} />
            </div>
          </div>
        </Alert>
      )}

      {/* Upload info */}
      {data?.uploadInfo && !uploadProgress && (
        <Alert variant="info" className="py-1 mb-2" style={{ fontSize: '0.8rem' }}>
          <i className="fas fa-info-circle me-1"></i>
          <strong>Archivo:</strong> {data.uploadInfo.fileName} | <strong>Corte:</strong> Día {data.uploadInfo.diaCorte} | <strong>Registros:</strong> {fmt(data.uploadInfo.totalRegistros)}
          {data.uploadInfo.notasReemplazo && <span className="text-warning ms-2"><i className="fas fa-sync-alt me-1"></i>{data.uploadInfo.notasReemplazo}</span>}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /><p className="mt-2 text-muted">Cargando datos JIT...</p></div>
      ) : !kpis ? (
        <Alert variant="warning" className="text-center py-5">
          <i className="fas fa-database" style={{ fontSize: '3rem' }}></i>
          <h5 className="mt-3">No hay datos JIT cargados</h5>
          <p className="text-muted">Sube un archivo JIT de Claro SAC para comenzar.</p>
          {canUpload && <Button variant="primary" onClick={() => fileInputRef.current?.click()}><i className="fas fa-upload me-2"></i>Subir primer archivo JIT</Button>}
        </Alert>
      ) : (
        <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">

          {/* ========== TAB: RESUMEN ========== */}
          <Tab eventKey="overview" title={<><i className="fas fa-chart-line me-1"></i> Resumen</>}>
            {/* KPIs */}
            <Row className="mb-3 g-2">
              <Col xs={6} md><KpiCard title="Total Registros" value={fmt(kpis.totalRegistros)} icon="fa-list" color="secondary" /></Col>
              <Col xs={6} md><KpiCard title="Contactados" value={fmt(kpis.totalContactados)} subtitle={`${fmtP(kpis.tasaContacto)} tasa contacto`} icon="fa-phone" color="primary" /></Col>
              <Col xs={6} md><KpiCard title="Aceptados" value={fmt(kpis.totalAcepta)} subtitle={`${fmtP(kpis.tasaAceptacion)} tasa aceptación`} icon="fa-check-circle" color="success" /></Col>
              <Col xs={6} md><KpiCard title="No Acepta" value={fmt(kpis.totalNoAcepta)} subtitle={`${kpis.totalContactados > 0 ? fmtP(kpis.totalNoAcepta / kpis.totalContactados) : '0%'} de contactados`} icon="fa-times-circle" color="danger" /></Col>
              <Col xs={6} md><KpiCard title="Agendados" value={fmt(kpis.totalAgendado)} icon="fa-calendar-check" color="warning" /></Col>
              <Col xs={6} md><KpiCard title="No Aplica" value={fmt(kpis.totalNoAplica)} icon="fa-ban" color="dark" /></Col>
            </Row>

            {/* Embudo + Clasificación */}
            <Row className="mb-3 g-2">
              <Col md={8}>
                <Card className="border-0 shadow-sm h-100">
                  <Card.Header className="bg-white border-0 py-2"><h6 className="mb-0"><i className="fas fa-filter me-2"></i>Embudo de Conversión</h6></Card.Header>
                  <Card.Body className="py-2">
                    <div className="d-flex align-items-center justify-content-around">
                      {[
                        { label: 'Registros', value: kpis.totalRegistros, color: '#6c757d' },
                        { label: 'Contactados', value: kpis.totalContactados, color: '#0d6efd' },
                        { label: 'Aceptados', value: kpis.totalAcepta, color: '#28a745' },
                      ].map((step, i, arr) => (
                        <React.Fragment key={step.label}>
                          <div className="text-center">
                            <h3 style={{ color: step.color, fontWeight: 700 }}>{fmt(step.value)}</h3>
                            <small className="text-muted">{step.label}</small>
                            {i > 0 && <div><Badge bg={i === 2 ? 'success' : 'primary'} className="mt-1">{arr[i-1].value > 0 ? `${((step.value/arr[i-1].value)*100).toFixed(1)}%` : '0%'}</Badge></div>}
                          </div>
                          {i < arr.length - 1 && <i className="fas fa-chevron-right text-muted"></i>}
                        </React.Fragment>
                      ))}
                    </div>
                    <ProgressBar className="mt-3" style={{ height: 10 }}>
                      <ProgressBar variant="success" now={kpis.totalRegistros > 0 ? (kpis.totalAcepta/kpis.totalRegistros)*100 : 0} />
                      <ProgressBar variant="primary" now={kpis.totalRegistros > 0 ? ((kpis.totalContactados-kpis.totalAcepta)/kpis.totalRegistros)*100 : 0} />
                      <ProgressBar variant="secondary" now={kpis.totalRegistros > 0 ? (kpis.totalNoContactados/kpis.totalRegistros)*100 : 0} />
                    </ProgressBar>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <ChartCard title="Distribución Clasificación" icon="fa-chart-pie">
                  <ReactEChartsCore echarts={echarts} option={clasifPieOpt} style={{ height: '200px' }} />
                </ChartCard>
              </Col>
            </Row>

            {/* Tendencia + Canal Pie */}
            <Row className="mb-3 g-2">
              <Col md={8}>
                <ChartCard title="Tendencia Diaria" icon="fa-chart-bar">
                  <ReactEChartsCore echarts={echarts} option={dailyTrendOpt} style={{ height: '320px' }} />
                </ChartCard>
              </Col>
              <Col md={4}>
                <ChartCard title="Distribución por Canal" icon="fa-pie-chart">
                  <ReactEChartsCore echarts={echarts} option={channelPieOpt} style={{ height: '320px' }} />
                </ChartCard>
              </Col>
            </Row>

            {/* Tasas + Productos */}
            <Row className="mb-3 g-2">
              <Col md={6}>
                <ChartCard title="Tasas Diarias (con promedio)" icon="fa-percentage">
                  <ReactEChartsCore echarts={echarts} option={ratesTrendOpt} style={{ height: '300px' }} />
                </ChartCard>
              </Col>
              <Col md={6}>
                <ChartCard title="Por Grupo de Transacción" icon="fa-boxes">
                  <ReactEChartsCore echarts={echarts} option={productBarOpt} style={{ height: '300px' }} />
                </ChartCard>
              </Col>
            </Row>
          </Tab>

          {/* ========== TAB: CANALES ========== */}
          <Tab eventKey="channels" title={<><i className="fas fa-broadcast-tower me-1"></i> Canales</>}>
            <Row className="mb-3 g-2">
              <Col md={12}>
                <ChartCard title="Comparativa de Canales: Registros vs Aceptados vs Tasa" icon="fa-chart-bar">
                  <ReactEChartsCore echarts={echarts} option={channelAcceptOpt} style={{ height: '350px' }} />
                </ChartCard>
              </Col>
            </Row>
            <Row className="g-2">
              {data?.byChannel?.map(ch => (
                <Col md={4} lg={3} key={ch.canalValidado}>
                  <Card className="border-0 shadow-sm mb-2" style={{ borderLeft: `4px solid ${CANAL_COLORS[ch.canalValidado] || '#adb5bd'}` }}>
                    <Card.Body className="p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h6 className="mb-0">{ch.canalValidado || 'SIN CANAL'}</h6>
                        <Badge bg={ch.tasaAceptacion > 0.1 ? 'success' : ch.tasaAceptacion > 0.05 ? 'warning' : 'danger'}>{fmtP(ch.tasaAceptacion)}</Badge>
                      </div>
                      <Row className="text-center g-1">
                        <Col><div className="bg-light rounded p-1"><small className="text-muted d-block" style={{fontSize:'0.65rem'}}>Registros</small><strong>{fmtK(ch.totalRegistros)}</strong></div></Col>
                        <Col><div className="bg-light rounded p-1"><small className="text-muted d-block" style={{fontSize:'0.65rem'}}>Contact.</small><strong className="text-primary">{fmtK(ch.totalContactados)}</strong></div></Col>
                        <Col><div className="bg-light rounded p-1"><small className="text-muted d-block" style={{fontSize:'0.65rem'}}>Acepta</small><strong className="text-success">{fmtK(ch.totalAcepta)}</strong></div></Col>
                      </Row>
                      <ProgressBar className="mt-2" style={{ height: 5 }}>
                        <ProgressBar variant="success" now={ch.totalRegistros > 0 ? (ch.totalAcepta/ch.totalRegistros)*100 : 0} />
                        <ProgressBar variant="primary" now={ch.totalRegistros > 0 ? ((ch.totalContactados-ch.totalAcepta)/ch.totalRegistros)*100 : 0} />
                      </ProgressBar>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </Tab>

          {/* ========== TAB: PRODUCTOS ========== */}
          <Tab eventKey="products" title={<><i className="fas fa-box me-1"></i> Productos</>}>
            <Row className="mb-3 g-2">
              <Col md={12}>
                <ChartCard title="Grupo de Transacción: Registros, Contactados, Aceptados" icon="fa-chart-bar">
                  <ReactEChartsCore echarts={echarts} option={productBarOpt} style={{ height: '350px' }} />
                </ChartCard>
              </Col>
            </Row>
            <Card className="border-0 shadow-sm mb-3">
              <Card.Header className="bg-white border-0 py-2"><h6 className="mb-0">Por Grupo Transacción</h6></Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead className="table-light">
                    <tr><th>Grupo</th><th className="text-end">Registros</th><th className="text-end">Contactados</th><th className="text-end">Aceptados</th><th className="text-end">Tasa Contacto</th><th className="text-end">Tasa Aceptación</th><th>Embudo</th></tr>
                  </thead>
                  <tbody>
                    {data?.byProduct?.map(p => {
                      const tc = p.totalRegistros > 0 ? p.totalContactados/p.totalRegistros : 0;
                      const ta = p.tasaAceptacion;
                      return (
                        <tr key={p.grupoTransaccion}>
                          <td><strong>{p.grupoTransaccion || 'N/A'}</strong></td>
                          <td className="text-end">{fmt(p.totalRegistros)}</td>
                          <td className="text-end">{fmt(p.totalContactados)}</td>
                          <td className="text-end text-success fw-bold">{fmt(p.totalAcepta)}</td>
                          <td className="text-end"><Badge bg={tc > 0.4 ? 'success' : tc > 0.3 ? 'warning' : 'danger'}>{fmtP(tc)}</Badge></td>
                          <td className="text-end"><Badge bg={ta > 0.1 ? 'success' : ta > 0.05 ? 'warning' : 'danger'}>{fmtP(ta)}</Badge></td>
                          <td style={{width: 120}}><ProgressBar style={{height:6}}>
                            <ProgressBar variant="success" now={p.totalRegistros > 0 ? (p.totalAcepta/p.totalRegistros)*100 : 0} />
                            <ProgressBar variant="primary" now={p.totalRegistros > 0 ? ((p.totalContactados-p.totalAcepta)/p.totalRegistros)*100 : 0} />
                          </ProgressBar></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-0 py-2"><h6 className="mb-0">Por Tipo de Producto</h6></Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead className="table-light">
                    <tr><th>Tipo</th><th className="text-end">Registros</th><th className="text-end">Contactados</th><th className="text-end">Aceptados</th><th className="text-end">Tasa Aceptación</th></tr>
                  </thead>
                  <tbody>
                    {data?.byProductType?.map(p => (
                      <tr key={p.tipoProducto}>
                        <td><strong>{p.tipoProducto || 'N/A'}</strong></td>
                        <td className="text-end">{fmt(p.totalRegistros)}</td>
                        <td className="text-end">{fmt(p.totalContactados)}</td>
                        <td className="text-end text-success fw-bold">{fmt(p.totalAcepta)}</td>
                        <td className="text-end"><Badge bg={p.tasaAceptacion > 0.1 ? 'success' : 'warning'}>{fmtP(p.tasaAceptacion)}</Badge></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          {/* ========== TAB: TIENDAS ========== */}
          <Tab eventKey="stores" title={<><i className="fas fa-store me-1"></i> Tiendas</>}>
            <Row className="mb-3 g-2">
              <Col md={12}>
                <ChartCard title="Ranking de Tiendas (registros + tasa aceptación)" icon="fa-trophy">
                  <ReactEChartsCore echarts={echarts} option={storeRankOpt} style={{ height: '350px' }} />
                </ChartCard>
              </Col>
            </Row>
            <Card className="border-0 shadow-sm">
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead className="table-light">
                    <tr><th>#</th><th>Tienda</th><th className="text-end">Registros</th><th className="text-end">Contactados</th><th className="text-end">Aceptados</th><th className="text-end">Tasa Contacto</th><th className="text-end">Tasa Aceptación</th><th>Performance</th></tr>
                  </thead>
                  <tbody>
                    {data?.byStore?.map((s, i) => {
                      const tc = s.totalRegistros > 0 ? s.totalContactados/s.totalRegistros : 0;
                      return (
                        <tr key={s.tienda}>
                          <td>{i+1}</td>
                          <td><strong>{(s.tienda||'').replace('Clic2Call_','')}</strong></td>
                          <td className="text-end">{fmt(s.totalRegistros)}</td>
                          <td className="text-end">{fmt(s.totalContactados)}</td>
                          <td className="text-end text-success fw-bold">{fmt(s.totalAcepta)}</td>
                          <td className="text-end"><Badge bg={tc > 0.4 ? 'success' : 'warning'}>{fmtP(tc)}</Badge></td>
                          <td className="text-end"><Badge bg={s.tasaAceptacion > 0.1 ? 'success' : s.tasaAceptacion > 0.05 ? 'warning' : 'danger'}>{fmtP(s.tasaAceptacion)}</Badge></td>
                          <td style={{width:100}}><ProgressBar style={{height:6}}>
                            <ProgressBar variant="success" now={s.totalRegistros > 0 ? (s.totalAcepta/s.totalRegistros)*100 : 0} />
                          </ProgressBar></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          {/* ========== TAB: CONCLUSIONES ========== */}
          <Tab eventKey="conclusions" title={<><i className="fas fa-clipboard-list me-1"></i> Conclusiones</>}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-0 py-2"><h6 className="mb-0">Top 15 Códigos de Conclusión</h6></Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead className="table-light"><tr><th>#</th><th>Código Conclusión</th><th>Clasificación</th><th className="text-end">Total</th><th>% del Total</th></tr></thead>
                  <tbody>
                    {data?.topConclusiones?.map((c, i) => {
                      const pct = kpis.totalRegistros > 0 ? (c.total/kpis.totalRegistros) : 0;
                      return (
                        <tr key={i}>
                          <td>{i+1}</td>
                          <td>{c.codigoConclusion}</td>
                          <td><Badge bg={c.clasificacion?.toLowerCase().includes('acepta') && !c.clasificacion?.toLowerCase().includes('no acepta') ? 'success' : c.clasificacion?.toLowerCase().includes('no acepta') ? 'danger' : 'secondary'}>{c.clasificacion || 'N/A'}</Badge></td>
                          <td className="text-end fw-bold">{fmt(c.total)}</td>
                          <td style={{width:120}}><ProgressBar now={pct*100} variant="primary" style={{height:6}} /><small className="text-muted">{fmtP(pct)}</small></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          {/* ========== TAB: REPORTE GERENCIAL ========== */}
          <Tab eventKey="executive" title={<><i className="fas fa-briefcase me-1"></i> Reporte Gerencial</>}>
            {execLoading ? (
              <div className="text-center py-5"><Spinner animation="border" variant="primary" /><p className="mt-2 text-muted">Generando reporte gerencial...</p></div>
            ) : !execReport ? (
              <Alert variant="info" className="text-center py-4">
                <i className="fas fa-chart-bar" style={{ fontSize: '2rem' }}></i>
                <p className="mt-2">Selecciona un período con datos para ver el reporte gerencial.</p>
                <Button variant="primary" size="sm" onClick={loadExecutiveReport}><i className="fas fa-sync me-1"></i> Cargar Reporte</Button>
              </Alert>
            ) : (
              <>
                {/* Resumen Ejecutivo */}
                <Alert variant="light" className="border shadow-sm mb-3">
                  <h6 className="mb-2"><i className="fas fa-file-alt me-2 text-primary"></i>Resumen Ejecutivo - {selectedPeriod}</h6>
                  <p className="mb-0" style={{ lineHeight: 1.7 }}>{execReport.executiveSummary}</p>
                </Alert>

                {/* KPIs */}
                <Row className="mb-3 g-2">
                  {[
                    { title: 'Inversión', value: fmtC(execReport.funnel?.inversion), icon: 'fa-dollar-sign', color: 'primary' },
                    { title: 'Impresiones', value: fmtK(execReport.funnel?.impresiones), icon: 'fa-eye', color: 'info' },
                    { title: 'Clicks', value: fmtK(execReport.funnel?.clicks), subtitle: `CTR: ${fmtP(execReport.funnel?.ctr)}`, icon: 'fa-mouse-pointer', color: 'secondary' },
                    { title: 'Leads Meta', value: fmtK(execReport.funnel?.leadsMeta), subtitle: `CPL: ${fmtC(execReport.funnel?.cplMeta)}`, icon: 'fa-bullseye', color: 'warning' },
                    { title: 'Aceptados JIT', value: fmt(execReport.funnel?.aceptados), subtitle: `CPA: ${fmtC(execReport.funnel?.cpaReal)}`, icon: 'fa-check-circle', color: 'success' },
                    { title: 'Conversión E2E', value: fmtP(execReport.funnel?.conversionTotal), subtitle: 'Leads → Aceptados', icon: 'fa-funnel-dollar', color: 'danger' },
                  ].map((k, i) => (
                    <Col xs={6} md={4} xl={2} key={i}><KpiCard {...k} small /></Col>
                  ))}
                </Row>

                {/* Embudo Visual */}
                <Card className="border-0 shadow-sm mb-3">
                  <Card.Header className="bg-white border-0 py-2"><h6 className="mb-0"><i className="fas fa-filter me-2"></i>Embudo Completo: Pauta Digital → SAC</h6></Card.Header>
                  <Card.Body className="py-3">
                    <div className="d-flex align-items-center justify-content-between flex-wrap">
                      {[
                        { label: 'Inversión', value: fmtC(execReport.funnel?.inversion), color: '#6c757d', raw: execReport.funnel?.inversion },
                        { label: 'Impresiones', value: fmtK(execReport.funnel?.impresiones), color: '#0d6efd', raw: execReport.funnel?.impresiones },
                        { label: 'Clicks', value: fmtK(execReport.funnel?.clicks), color: '#0dcaf0', raw: execReport.funnel?.clicks },
                        { label: 'Leads Meta', value: fmtK(execReport.funnel?.leadsMeta), color: '#ffc107', raw: execReport.funnel?.leadsMeta },
                        { label: 'Registros JIT', value: fmt(execReport.funnel?.registrosJit), color: '#fd7e14', raw: execReport.funnel?.registrosJit },
                        { label: 'Contactados', value: fmt(execReport.funnel?.contactados), color: '#0d6efd', raw: execReport.funnel?.contactados },
                        { label: 'Aceptados', value: fmt(execReport.funnel?.aceptados), color: '#28a745', raw: execReport.funnel?.aceptados },
                      ].map((step, i, arr) => (
                        <React.Fragment key={step.label}>
                          <div className="text-center" style={{ flex: 1, minWidth: 75 }}>
                            <div style={{ color: step.color, fontWeight: 700, fontSize: '1rem' }}>{step.value}</div>
                            <small className="text-muted" style={{ fontSize: '0.65rem' }}>{step.label}</small>
                            {i > 0 && arr[i-1].raw > 0 && (
                              <div><Badge bg="light" text="dark" style={{ fontSize: '0.6rem' }}>{((step.raw/arr[i-1].raw)*100).toFixed(1)}%</Badge></div>
                            )}
                          </div>
                          {i < arr.length - 1 && <i className="fas fa-chevron-right text-muted" style={{ fontSize: '0.6rem' }}></i>}
                        </React.Fragment>
                      ))}
                    </div>
                    <ProgressBar className="mt-3" style={{ height: 10 }}>
                      <ProgressBar variant="success" now={execReport.funnel?.registrosJit > 0 ? (execReport.funnel?.aceptados/execReport.funnel?.registrosJit)*100 : 0} />
                      <ProgressBar variant="primary" now={execReport.funnel?.registrosJit > 0 ? ((execReport.funnel?.contactados-execReport.funnel?.aceptados)/execReport.funnel?.registrosJit)*100 : 0} />
                      <ProgressBar variant="secondary" now={execReport.funnel?.registrosJit > 0 ? ((execReport.funnel?.registrosJit-execReport.funnel?.contactados)/execReport.funnel?.registrosJit)*100 : 0} />
                    </ProgressBar>
                    <div className="d-flex justify-content-between mt-1" style={{ fontSize: '0.7rem' }}>
                      <span className="text-success">Aceptados {fmtP(execReport.funnel?.tasaAceptacion)}</span>
                      <span className="text-primary">Contactados {fmtP(execReport.funnel?.tasaContacto)}</span>
                      <span className="text-secondary">No contactados</span>
                    </div>
                  </Card.Body>
                </Card>

                {/* Tendencia + Canales */}
                <Row className="mb-3 g-2">
                  <Col md={8}>
                    <ChartCard title="Tendencia Diaria Cruzada: Meta vs JIT" icon="fa-chart-area">
                      <ReactEChartsCore echarts={echarts} style={{ height: '320px' }} option={{
                        tooltip: { trigger: 'axis' },
                        legend: { data: ['Inversión', 'Leads Meta', 'Registros JIT', 'Aceptados'], bottom: 0, textStyle: { fontSize: 10 } },
                        grid: { left: '3%', right: '5%', bottom: '15%', containLabel: true },
                        xAxis: { type: 'category', data: (execReport.dailyCross||[]).map(d => `Día ${d.dia}`) },
                        yAxis: [{ type: 'value', name: 'Cantidad' }, { type: 'value', name: '$', position: 'right' }],
                        series: [
                          { name: 'Inversión', type: 'line', yAxisIndex: 1, data: (execReport.dailyCross||[]).map(d => d.inversionMeta?.toFixed(2)), itemStyle: { color: '#6c757d' }, lineStyle: { width: 1, type: 'dotted' }, areaStyle: { opacity: 0.03 } },
                          { name: 'Leads Meta', type: 'bar', data: (execReport.dailyCross||[]).map(d => Math.round(d.leadsMeta)), itemStyle: { color: '#ffc107' }, barMaxWidth: 16 },
                          { name: 'Registros JIT', type: 'bar', data: (execReport.dailyCross||[]).map(d => d.registrosJit), itemStyle: { color: '#0d6efd' }, barMaxWidth: 16 },
                          { name: 'Aceptados', type: 'line', data: (execReport.dailyCross||[]).map(d => d.aceptadosJit), itemStyle: { color: '#28a745' }, lineStyle: { width: 3 }, symbol: 'circle', symbolSize: 6 },
                        ],
                        dataZoom: [{ type: 'inside' }],
                      }} />
                    </ChartCard>
                  </Col>
                  <Col md={4}>
                    <ChartCard title="Rendimiento por Canal" icon="fa-broadcast-tower">
                      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                        {(execReport.byChannel||[]).map((ch, i) => (
                          <div key={i} className="mb-2 p-2 bg-light rounded">
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <strong style={{ fontSize: '0.75rem' }}>{ch.canal}</strong>
                              <Badge bg={ch.tasaAceptacion > 0.1 ? 'success' : ch.tasaAceptacion > 0.05 ? 'warning' : 'danger'} style={{ fontSize: '0.65rem' }}>{fmtP(ch.tasaAceptacion)}</Badge>
                            </div>
                            <div className="d-flex justify-content-between" style={{ fontSize: '0.65rem' }}>
                              <span>{fmt(ch.registros)} reg.</span>
                              <span className="text-primary">{fmt(ch.contactados)} cont.</span>
                              <span className="text-success fw-bold">{fmt(ch.aceptados)} acept.</span>
                            </div>
                            <ProgressBar className="mt-1" style={{ height: 4 }}>
                              <ProgressBar variant="success" now={ch.registros > 0 ? (ch.aceptados/ch.registros)*100 : 0} />
                              <ProgressBar variant="primary" now={ch.registros > 0 ? ((ch.contactados-ch.aceptados)/ch.registros)*100 : 0} />
                            </ProgressBar>
                          </div>
                        ))}
                      </div>
                    </ChartCard>
                  </Col>
                </Row>

                {/* Embudo por Producto */}
                <Card className="border-0 shadow-sm mb-3">
                  <Card.Header className="bg-white border-0 py-2"><h6 className="mb-0"><i className="fas fa-boxes me-2"></i>Embudo por Producto (Meta Ads → JIT SAC)</h6></Card.Header>
                  <Card.Body className="p-0">
                    <Table responsive hover className="mb-0" style={{ fontSize: '0.8rem' }}>
                      <thead className="table-light">
                        <tr><th>Producto</th><th className="text-end">Inversión</th><th className="text-end">Leads Meta</th><th className="text-end">Registros JIT</th><th className="text-end">Contactados</th><th className="text-end">Aceptados</th><th className="text-end">CPL</th><th className="text-end">CPA Real</th><th className="text-end">Tasa Acept.</th></tr>
                      </thead>
                      <tbody>
                        {(execReport.byProduct||[]).map((p, i) => (
                          <tr key={i}>
                            <td><span className="d-inline-block rounded-circle me-1" style={{ width: 8, height: 8, backgroundColor: PRODUCT_COLORS[p.producto]||'#adb5bd' }}></span><strong>{p.producto}</strong></td>
                            <td className="text-end">{fmtC(p.inversion)}</td>
                            <td className="text-end">{fmtK(p.leadsMeta)}</td>
                            <td className="text-end">{fmt(p.registrosJit)}</td>
                            <td className="text-end">{fmt(p.contactados)}</td>
                            <td className="text-end text-success fw-bold">{fmt(p.aceptados)}</td>
                            <td className="text-end">{p.leadsMeta > 0 ? fmtC(p.inversion/p.leadsMeta) : '-'}</td>
                            <td className="text-end">{p.aceptados > 0 ? fmtC(p.cpaReal) : <span className="text-muted">-</span>}</td>
                            <td className="text-end"><Badge bg={p.tasaAceptacion > 0.1 ? 'success' : p.tasaAceptacion > 0.05 ? 'warning' : 'danger'}>{fmtP(p.tasaAceptacion)}</Badge></td>
                          </tr>
                        ))}
                        <tr className="table-secondary fw-bold">
                          <td>TOTAL</td>
                          <td className="text-end">{fmtC(execReport.funnel?.inversion)}</td>
                          <td className="text-end">{fmtK(execReport.funnel?.leadsMeta)}</td>
                          <td className="text-end">{fmt(execReport.funnel?.registrosJit)}</td>
                          <td className="text-end">{fmt(execReport.funnel?.contactados)}</td>
                          <td className="text-end text-success">{fmt(execReport.funnel?.aceptados)}</td>
                          <td className="text-end">{fmtC(execReport.funnel?.cplMeta)}</td>
                          <td className="text-end">{fmtC(execReport.funnel?.cpaReal)}</td>
                          <td className="text-end">{fmtP(execReport.funnel?.tasaAceptacion)}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>

                {/* Métricas comparativas */}
                <Row className="g-2">
                  <Col md={3}><Card className="border-0 shadow-sm text-center p-3"><small className="text-muted">CPL Meta</small><h4 className="text-info mb-0">{fmtC(execReport.funnel?.cplMeta)}</h4><small className="text-muted">Inversión / Leads</small></Card></Col>
                  <Col md={3}><Card className="border-0 shadow-sm text-center p-3"><small className="text-muted">CPA Real</small><h4 className="text-danger mb-0">{fmtC(execReport.funnel?.cpaReal)}</h4><small className="text-muted">Inversión / Aceptados</small></Card></Col>
                  <Col md={3}><Card className="border-0 shadow-sm text-center p-3"><small className="text-muted">Multiplicador</small><h4 className="text-warning mb-0">{execReport.funnel?.cplMeta > 0 ? `${(execReport.funnel?.cpaReal/execReport.funnel?.cplMeta).toFixed(1)}x` : '-'}</h4><small className="text-muted">CPA / CPL</small></Card></Col>
                  <Col md={3}><Card className="border-0 shadow-sm text-center p-3"><small className="text-muted">ROI Contacto</small><h4 className="text-success mb-0">{fmtP(execReport.funnel?.tasaContacto)}</h4><small className="text-muted">Registros → Contactados</small></Card></Col>
                </Row>
              </>
            )}
          </Tab>

          {/* ========== TAB: REPORTE DIGITAL ========== */}
          <Tab eventKey="digital" title={<><i className="fas fa-ad me-1"></i> Reporte Digital</>}>
            <ClaroDigitalReportTab jitData={data} dateFrom={selectedPeriod ? `${selectedPeriod}-01` : undefined} dateTo={selectedPeriod ? `${selectedPeriod}-31` : undefined} />
          </Tab>

          {/* ========== TAB: HISTORIAL ========== */}
          <Tab eventKey="history" title={<><i className="fas fa-history me-1"></i> Historial</>}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-0 d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Historial de Cargas</h6>
                {canUpload && <Button variant="outline-primary" size="sm" onClick={() => fileInputRef.current?.click()}><i className="fas fa-upload me-1"></i> Subir archivo</Button>}
              </Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0" style={{ fontSize: '0.85rem' }}>
                  <thead className="table-light"><tr><th>Archivo</th><th>Período</th><th>Corte</th><th className="text-end">Registros</th><th className="text-end">Contactados</th><th className="text-end">Aceptados</th><th>Estado</th><th>Fecha</th><th></th></tr></thead>
                  <tbody>
                    {uploads.map(u => (
                      <tr key={u.id} className={u.estado === 'REEMPLAZADO' ? 'text-muted' : ''}>
                        <td><small>{u.fileName}</small>{u.notasReemplazo && <div><small className="text-warning"><i className="fas fa-sync-alt me-1"></i>{u.notasReemplazo}</small></div>}</td>
                        <td>{u.periodoReporte}</td><td>Día {u.diaCorte}</td>
                        <td className="text-end">{fmt(u.totalRegistros)}</td><td className="text-end">{fmt(u.totalContactados)}</td><td className="text-end">{fmt(u.totalAceptados)}</td>
                        <td><Badge bg={u.estado === 'COMPLETADO' ? 'success' : 'secondary'}>{u.estado}</Badge></td>
                        <td><small>{new Date(u.fechaCarga).toLocaleDateString('es-EC')}</small></td>
                        <td>{canUpload && u.estado === 'COMPLETADO' && <Button variant="outline-danger" size="sm" onClick={() => handleDeleteUpload(u.id, u.fileName)}><i className="fas fa-trash"></i></Button>}</td>
                      </tr>
                    ))}
                    {!uploads.length && <tr><td colSpan="9" className="text-center text-muted py-4">No hay cargas</td></tr>}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

        </Tabs>
      )}
    </Container>
  );
};

export default ClaroJitDashboard;
